import { useId, useMemo, useState } from "react";
import { Link } from "wouter";
import { cn } from "@/lib/utils";
import { useGetStartedChecklist } from "@/hooks/useGetStartedChecklist";
import { SidebarChevronIcon } from "@/components/Icons/SidebarChevronIcon";

type SidebarGetStartedProps = {
  className?: string;
  onPurchaseCredits?: () => void;
};

/** Align with `useMediaQueryMatch("md")` — expanded sidebar / desktop nav. */
function getInitialOpenForViewport(): boolean {
  if (typeof window === "undefined") {
    return true;
  }
  return window.matchMedia("(min-width: 768px)").matches;
}

function ChecklistItemRow({
  item,
  isCurrent
}: {
  item: {
    id: string;
    label: string;
    done: boolean;
    href?: string;
    onClick?: () => void;
  };
  isCurrent: boolean;
}) {
  const rowClassName =
    "flex items-center gap-2 rounded-lg px-2 py-1 transition-colors hover:bg-gray-light-500 dark:hover:bg-border w-full text-left";

  const indicator = item.done ? (
    <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[rgba(255,255,255,0.25)] bg-[#3179ff]">
      <svg viewBox="0 0 10 10" className="h-2.5 w-2.5 text-white" fill="none">
        <path
          d="M2 5L4 7L8 3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  ) : (
    <div
      className={cn(
        "h-4 w-4 shrink-0 rounded-[4px] border-[1.5px] border-border bg-background",
        isCurrent && "border-[#3179ff] ring-1 ring-[#3179ff]/35"
      )}
    />
  );

  const label = (
    <span
      className={cn(
        "text-[13px] font-medium",
        item.done && "text-foreground line-through decoration-solid",
        !item.done && isCurrent && "text-foreground",
        !item.done && !isCurrent && "text-muted-foreground"
      )}
    >
      {item.label}
    </span>
  );

  if (item.onClick) {
    return (
      <button type="button" onClick={item.onClick} className={rowClassName}>
        {indicator}
        {label}
      </button>
    );
  }

  if (item.href) {
    return (
      <Link href={item.href} asChild>
        <a className={rowClassName}>
          {indicator}
          {label}
        </a>
      </Link>
    );
  }

  return (
    <div className={rowClassName}>
      {indicator}
      {label}
    </div>
  );
}

export function SidebarGetStarted({ className, onPurchaseCredits }: SidebarGetStartedProps) {
  const panelId = useId();
  const [isOpen, setIsOpen] = useState(getInitialOpenForViewport);
  const { items } = useGetStartedChecklist(true, onPurchaseCredits);

  const doneCount = items.filter((i) => i.done).length;
  const firstIncompleteIndex = useMemo(
    () => items.findIndex((i) => !i.done),
    [items]
  );

  if (doneCount === items.length) {
    return null;
  }

  return (
    <div className={cn("rounded-lg border border-border bg-gray-light-400 pt-2 dark:bg-border", className)}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className="flex w-full items-center gap-2 px-3 pb-2 text-left"
      >
        <span className="text-[13px] font-medium text-foreground">Get Started</span>
        <span className="min-w-0 flex-1" aria-hidden />
        <span className="rounded border border-border bg-background px-2 py-0.5 text-[12px] font-medium text-foreground tabular-nums">
          {doneCount}/{items.length}
        </span>
        <div className="relative size-[20px] shrink-0 overflow-clip text-foreground md:hidden">
          <div className="absolute inset-0 flex items-center justify-center" style={{ containerType: "size" }}>
            <div
              className={cn(
                "flex h-[100cqh] w-[100cqw] flex-none -scale-x-100 transition-transform duration-300 ease-[cubic-bezier(0.33,1,0.68,1)]",
                isOpen && "-rotate-180"
              )}
            >
              <div className="relative size-full overflow-clip">
                <div className="absolute left-[6px] top-[8px] h-[4px] w-[8px]">
                  <div className="absolute inset-[-29.46%_-14.73%_-20.83%_-14.73%]">
                    <SidebarChevronIcon className="block size-full max-w-none" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </button>

      <div
        id={panelId}
        className={cn(
          "grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.33,1,0.68,1)]",
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="flex flex-col gap-1 rounded-b-lg rounded-t-lg border-t border-border bg-background p-1">
            {items.map((item, index) => {
              const isCurrent = !item.done && index === firstIncompleteIndex;
              return (
                <ChecklistItemRow key={item.id} item={item} isCurrent={isCurrent} />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
